/**
 * @file        src/pages/package/AddHoliday.jsx
 * @description Create / edit a Domestic & International Holiday tile. Image is
 *              stored as a URL. Reused for /package/holiday/new and .../:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-09-15 Claude — Language is a locked English-only dropdown reading
 *                          "English", not a free-text box defaulting to "en".
 *                          It accepted any 2-8 character string for a storefront
 *                          that serves English only, so a row saved as hi/mr was
 *                          content nobody could read. Options come from
 *                          lib/languages.js — one list for all five forms.
 *   - 2026-07-01 Claude — Initial (Package Management: Holiday slice).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../lib/languages';

const schema = z.object({
  title: z.string().trim().min(1, 'Required').max(200),
  holiday_type: z.enum(['domestic', 'international']),
  image: z.union([z.string().trim().url('Must be a valid URL'), z.literal('')]).optional(),
  description: z.string().trim().max(5000).optional(),
  language: z.enum(['en'], { errorMap: () => ({ message: 'English only' }) }),
  is_active: z.boolean(),
});

const empty = {
  title: '', holiday_type: 'domestic', image: '', description: '',
  language: DEFAULT_LANGUAGE, is_active: true,
};

export default function AddHoliday() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.holidays.one(id),
    queryFn: () => get(`/holidays/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const h = existing.data;
      setForm({
        title: h.title || '',
        holiday_type: h.holiday_type || 'domestic',
        image: h.image || '',
        description: h.description || '',
        language: h.language || DEFAULT_LANGUAGE,
        is_active: !!h.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/holidays/${id}`, body) : post('/holidays', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Holiday updated' : 'Holiday added', `${form.title} saved.`);
      qc.invalidateQueries({ queryKey: ['holidays'] });
      navigate('/package/holiday');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrs = {};
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
      return setErrors(fieldErrs);
    }
    setErrors({});
    const body = { ...parsed.data };
    if (!body.image) delete body.image;
    if (!body.description) delete body.description;
    mut.mutate(body);
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Holiday' : 'Add Domestic & International Holiday'}
        subtitle="Package Management"
        actions={<button className="btn ghost" onClick={() => navigate('/package/holiday')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Title" required err={errors.title}>
            <input value={form.title} onChange={onChange('title')} placeholder="e.g. Dubai Tour" />
          </Field>
          <Field label="Type" required err={errors.holiday_type}>
            <Select
              value={form.holiday_type}
              onChange={(v) => setForm({ ...form, holiday_type: v })}
              options={[{ value: 'domestic', label: 'Domestic' }, { value: 'international', label: 'International' }]}
            />
          </Field>
          <Field label="Language" required err={errors.language}
                 hint="English is the only language the website serves.">
            <Select
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              options={LANGUAGE_OPTIONS}
            />
          </Field>

          <Field label="Image" err={errors.image} full>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          </Field>

          <Field label="Description" err={errors.description} full>
            <textarea rows={3} value={form.description} onChange={onChange('description')}
                      placeholder="Optional short description." />
          </Field>

          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active (visible to the website)</span>
            </label>
          </Field>

          <div className="form-actions">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={() => setForm(empty)}>Reset</button>
            )}
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Holiday'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
